import type { CoordinatorRole } from './constants';

/**
 * What a coordinator login may do (SPEC §1.1L) — the one spelling of the
 * question, the way `isExternal` is for teams. The API asks it in
 * `requireCoordinator` against the capability each route declares, and in the
 * serialisers that leave comms out of a payload; the SPA asks it before drawing
 * a control. Nothing else looks at a role.
 *
 * A capability names what a request touches, not a screen: `comms:read` covers
 * the thread, the AI summary and digest, and every trace of what was sent or
 * published, wherever those appear.
 */
export const CAPABILITIES = [
  /** The board, posts, map, handovers, teams, problems — and the change stream. */
  'plan:read',
  /** Shifts, autofill, posts, windows, photos, routes, sectors, teams. */
  'plan:write',
  /** The roster: profile, shifts, confirmation state, tags, notes between teams. */
  'people:read',
  /** Add, edit, deactivate, tags, confirmation override, Ne pride, import. */
  'people:write',
  /** The mail thread, AI summary and digest, send and publication status. */
  'comms:read',
  /** Sporočila, templates, sends, free-form mail, zabeležke, triage, AI drafts. */
  'comms:write',
  /** Minting a link, publishing, reverting, sending navodila. */
  'publish',
  /** Check-in and check-out, resolving a problem, the handover tick. */
  'live:act',
  /** Dnevnik and every per-entity history drawn from it. */
  'audit:read',
  /** Writing a note on a volunteer to the other team. */
  'notes:write',
  /** Granting, changing and revoking logins (Nastavitve → Uporabniki). */
  'access:manage',
  /** Event identity and the shift-planning numbers. */
  'settings:write',
] as const;
export type Capability = (typeof CAPABILITIES)[number];

/**
 * The marshal team holds everything — a route or control that asks for any
 * capability can never refuse them. The organisation reads the plan and the
 * roster and writes notes.
 */
export const ROLE_CAPABILITIES: Record<CoordinatorRole, readonly Capability[]> = {
  admin: CAPABILITIES,
  coordinator: CAPABILITIES,
  organizer: ['plan:read', 'people:read', 'notes:write'],
};

/**
 * What a route declares it needs: a capability, or `'session'` for anything a
 * signed-in coordinator of any role may do (their own name, the event).
 */
export type RouteAccess = Capability | 'session';

export function capabilitiesOf(role: CoordinatorRole): readonly Capability[] {
  return ROLE_CAPABILITIES[role];
}

export function can(role: CoordinatorRole, capability: Capability): boolean {
  return ROLE_CAPABILITIES[role].includes(capability);
}

/** Whether a role holds every capability — what a route that declares none requires. */
export function holdsAll(role: CoordinatorRole): boolean {
  return CAPABILITIES.every((c) => can(role, c));
}
